import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardCheck, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { dateTime } from "@/lib/format";
import { ConfirmDialog, EmptyState, ErrorState, Field, StatusBadge, TableSkeleton, rowCls, tdCls, thCls } from "@/components/admin/AdminUI";
import { VariantPicker } from "@/components/admin/VariantPicker";

export const StockCounts = ({ warehouses, canCount }) => {
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ warehouse_id: "", note: "" });
  const [active, setActive] = useState(null); // count detail
  const [lines, setLines] = useState([]);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api.get("/admin/inventory/counts");
      setList(r.data.items || []);
    } catch (e) {
      setError(errMsg(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCount = async (id) => {
    try {
      const r = await api.get(`/admin/inventory/counts/${id}`);
      setActive(r.data);
      setLines((r.data.lines || []).map((l) => ({ ...l, counted: String(l.counted) })));
      setPicked(null);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/inventory/counts", createForm);
      toast.success(`${r.data.count_number} opened`);
      setCreateOpen(false);
      setCreateForm({ warehouse_id: "", note: "" });
      await load();
      openCount(r.data.id);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const saveLines = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/admin/inventory/counts/${active.id}/lines`, { lines: lines.map((l) => ({ variant_id: l.variant_id, counted: Number(l.counted || 0) })) });
      setActive(r.data);
      setLines((r.data.lines || []).map((l) => ({ ...l, counted: String(l.counted) })));
      toast.success("Count lines saved");
      return true;
    } catch (err) {
      toast.error(errMsg(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const ok = await saveLines();
      if (!ok) return;
      const r = await api.post(`/admin/inventory/counts/${active.id}/apply`);
      setActive(r.data);
      toast.success(`${r.data.count_number} applied — ${(r.data.results || []).filter((x) => x.delta !== 0).length} corrections posted`);
      setConfirmApply(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const addLine = (v) => {
    if (lines.some((l) => l.variant_id === v.id)) return toast.error("Variant already on this count");
    setLines([...lines, { variant_id: v.id, sku: v.sku, size: v.size, color: v.color, expected_on_hand: null, counted: "" }]);
    setPicked(null);
  };

  const open = active?.status === "open";

  return (
    <div data-testid="stock-counts-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-muted-foreground">Physical counts post correction movements for every discrepancy when applied.</p>
        {canCount && <Button onClick={() => { setCreateForm({ warehouse_id: warehouses.find((w) => w.is_default)?.id || warehouses[0]?.id || "", note: "" }); setCreateOpen(true); }} className="rounded-lg" data-testid="count-new-button"><Plus className="h-4 w-4 mr-2" />New count</Button>}
      </div>
      <div className="bg-card border rounded-xl overflow-hidden">
        {error ? <ErrorState message={error} onRetry={load} /> : list === null ? <TableSkeleton cols={5} /> : list.length === 0 ? <EmptyState icon={ClipboardCheck} title="No counts yet" description="Open a count for a warehouse, enter what you physically find, then apply it." /> : (
          <Table data-testid="stock-counts-table">
            <TableHeader className="bg-secondary/60"><TableRow><TableHead className={thCls}>Count</TableHead><TableHead className={thCls}>Warehouse</TableHead><TableHead className={thCls}>Status</TableHead><TableHead className={`${thCls} text-right`}>Lines</TableHead><TableHead className={thCls}>Created</TableHead><TableHead className={thCls}>Applied</TableHead></TableRow></TableHeader>
            <TableBody>
              {list.map((c) => (
                <TableRow key={c.id} className={`${rowCls} cursor-pointer`} onClick={() => openCount(c.id)} data-testid="stock-count-row">
                  <TableCell className={`${tdCls} font-medium font-mono text-xs`}>{c.count_number}</TableCell>
                  <TableCell className={tdCls}>{c.warehouse_code}</TableCell>
                  <TableCell className={tdCls}><StatusBadge status={c.status} /></TableCell>
                  <TableCell className={`${tdCls} text-right tabular`}>{c.lines?.length || 0}</TableCell>
                  <TableCell className={`${tdCls} text-muted-foreground`}>{dateTime(c.created_at)}<span className="block text-xs">{c.created_by}</span></TableCell>
                  <TableCell className={`${tdCls} text-muted-foreground`}>{c.applied_at ? dateTime(c.applied_at) : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="count-create-dialog">
          <DialogHeader><DialogTitle className="font-display text-3xl font-medium">Open a stock count</DialogTitle><DialogDescription>One warehouse per count. Lines are added as you count.</DialogDescription></DialogHeader>
          <form onSubmit={create} className="space-y-4 pt-2">
            <Field label="Warehouse" htmlFor="cnt-wh" required>
              <Select value={createForm.warehouse_id} onValueChange={(v) => setCreateForm({ ...createForm, warehouse_id: v })}>
                <SelectTrigger id="cnt-wh" data-testid="count-warehouse-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{warehouses.filter((w) => w.status === "active").map((w) => <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Note" htmlFor="cnt-note"><Textarea id="cnt-note" rows={2} value={createForm.note} onChange={(e) => setCreateForm({ ...createForm, note: e.target.value })} data-testid="count-note-input" /></Field>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button><Button type="submit" disabled={busy || !createForm.warehouse_id} data-testid="count-create-submit">{busy ? "Opening…" : "Open count"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="count-detail-dialog">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-3xl font-medium flex items-center gap-3">{active.count_number} <StatusBadge status={active.status} /></DialogTitle>
                <DialogDescription>{active.warehouse_code} · opened {dateTime(active.created_at)} by {active.created_by}{active.note ? ` · ${active.note}` : ""}</DialogDescription>
              </DialogHeader>
              {open && canCount && (
                <div className="pt-2"><Field label="Add variant to count"><VariantPicker value={picked} onSelect={addLine} testId="count-variant-picker" exclude={lines.map((l) => l.variant_id)} /></Field></div>
              )}
              <div className="border rounded-xl overflow-hidden mt-2">
                <Table>
                  <TableHeader className="bg-secondary/60"><TableRow><TableHead className={thCls}>SKU</TableHead><TableHead className={thCls}>Variant</TableHead><TableHead className={`${thCls} text-right`}>Expected</TableHead><TableHead className={`${thCls} text-right`}>Counted</TableHead><TableHead className={`${thCls} text-right`}>Delta</TableHead>{open && canCount && <TableHead className={thCls} />}</TableRow></TableHeader>
                  <TableBody>
                    {lines.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">No lines yet.</TableCell></TableRow>}
                    {lines.map((l, i) => {
                      const expected = l.current_on_hand ?? l.expected_on_hand;
                      const delta = l.counted === "" || expected === null || expected === undefined ? null : Number(l.counted) - Number(expected);
                      return (
                        <TableRow key={l.variant_id} className={rowCls} data-testid="count-line-row">
                          <TableCell className={`${tdCls} font-mono text-xs`}>{l.sku}</TableCell>
                          <TableCell className={tdCls}>{l.color} / {l.size}</TableCell>
                          <TableCell className={`${tdCls} text-right tabular text-muted-foreground`}>{expected ?? "—"}</TableCell>
                          <TableCell className={`${tdCls} text-right`}>{open && canCount ? <Input type="number" min={0} value={l.counted} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, counted: e.target.value } : x)))} className="h-8 w-24 ml-auto text-right tabular" aria-label="Counted quantity" data-testid="count-line-input" /> : <span className="tabular">{l.counted}</span>}</TableCell>
                          <TableCell className={`${tdCls} text-right tabular font-semibold ${delta > 0 ? "text-[hsl(var(--accent-forest))]" : delta < 0 ? "text-destructive" : ""}`}>{delta === null ? "—" : delta > 0 ? `+${delta}` : delta}</TableCell>
                          {open && canCount && <TableCell className={tdCls}><Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setLines(lines.filter((_, j) => j !== i))} aria-label="Remove line"><Trash2 className="h-4 w-4" /></Button></TableCell>}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {active.status === "applied" && active.results && (
                <p className="text-xs text-muted-foreground">Applied {dateTime(active.applied_at)} by {active.applied_by} · {active.results.filter((r) => r.delta !== 0).length} corrections.</p>
              )}
              {open && canCount && (
                <DialogFooter className="pt-2">
                  <Button variant="outline" onClick={saveLines} disabled={busy} data-testid="count-save-lines-button">Save lines</Button>
                  <Button onClick={() => setConfirmApply(true)} disabled={busy || lines.length === 0 || lines.some((l) => l.counted === "")} data-testid="count-apply-button"><ClipboardCheck className="h-4 w-4 mr-2" />Apply count</Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={confirmApply} onOpenChange={setConfirmApply} title="Apply this count?" description="On-hand balances are corrected to the counted quantities and a movement is posted per discrepancy. This cannot be undone." confirmLabel="Apply count" busy={busy} onConfirm={apply} />
    </div>
  );
};
